/* ========================================
   MADISON THEME JS
   Vanilla JS, <10KB target
   ======================================== */

(function () {
  'use strict';

  /* ========================================
     1. CART API
     ======================================== */
  const Cart = {
    async add(variantId, quantity = 1, sellingPlan = null) {
      const item = { id: variantId, quantity };
      if (sellingPlan) item.selling_plan = parseInt(sellingPlan);
      const res = await fetch(window.theme.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      if (!res.ok) throw new Error('Failed to add to cart');
      const data = await res.json();
      await this.refresh();
      return data;
    },

    async change(line, quantity) {
      const res = await fetch(window.theme.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      if (!res.ok) throw new Error('Failed to update cart');
      const data = await res.json();
      await this.refresh();
      return data;
    },

    async get() {
      const res = await fetch(window.theme.routes.cart_url + '.js');
      return res.json();
    },

    async refresh() {
      const cart = await this.get();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      return cart;
    },
  };

  /* ========================================
     2. CART UI UPDATES
     ======================================== */
  document.addEventListener('cart:updated', (e) => {
    const cart = e.detail;

    // Update cart count badges
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? '' : 'none';
    });

    // Update cart subtotal
    document.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
      el.textContent = formatMoney(cart.total_price);
    });

    // Update checkout button price
    document.querySelectorAll('[data-cart-checkout-price]').forEach((el) => {
      el.textContent = formatMoney(cart.total_price);
    });

    // Calculate savings from compare_at_price
    var totalCompare = 0;
    var totalPrice = 0;
    cart.items.forEach(function(item) {
      var comparePrice = item.original_line_price || item.final_line_price;
      totalCompare += comparePrice;
      totalPrice += item.final_line_price;
    });
    var savings = totalCompare - totalPrice;

    // Update compare total
    document.querySelectorAll('[data-cart-compare-total]').forEach(function(el) {
      if (savings > 0) {
        el.textContent = formatMoney(totalCompare);
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });

    // Update savings row
    document.querySelectorAll('[data-cart-savings]').forEach(function(el) {
      if (savings > 0) {
        el.style.display = '';
        var amountEl = el.querySelector('[data-cart-savings-amount]');
        if (amountEl) amountEl.textContent = formatMoney(savings);
      } else {
        el.style.display = 'none';
      }
    });

    // Update cart drawer body
    const drawerBody = document.querySelector('[data-cart-drawer-body]');
    const drawerFooter = document.querySelector('[data-cart-drawer-footer]');
    if (drawerBody) {
      if (cart.item_count === 0) {
        renderEmptyCart(drawerBody);
        if (drawerFooter) drawerFooter.style.display = 'none';
      } else {
        // Fetch product data to get compare_at_price for each unique product
        const uniqueHandles = [...new Set(cart.items.map(item => item.handle))];
        Promise.all(uniqueHandles.map(handle =>
          fetch('/products/' + handle + '.js').then(r => r.json()).catch(() => null)
        )).then(products => {
          const variantMap = {};
          const sellingPlanMap = {};
          products.forEach(product => {
            if (!product) return;
            product.variants.forEach(v => { variantMap[v.id] = v; });
            // Build selling plan lookup: variantId -> { sellingPlanId, name, price, savePct }
            if (product.selling_plan_groups && product.selling_plan_groups.length > 0) {
              product.variants.forEach(v => {
                const alloc = v.selling_plan_allocations && v.selling_plan_allocations[0];
                if (alloc) {
                  const savePct = v.price > 0 ? Math.round((1 - alloc.price / v.price) * 100) : 0;
                  sellingPlanMap[v.id] = {
                    sellingPlanId: alloc.selling_plan_id,
                    name: alloc.selling_plan_name || 'Subscribe & Save',
                    price: alloc.price,
                    savePct: savePct
                  };
                }
              });
            }
          });

          let html = '';
          cart.items.forEach((item, i) => {
            const variant = variantMap[item.variant_id];
            const compareAt = variant && variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : 0;
            const compareAtLine = compareAt * item.quantity;
            const sp = sellingPlanMap[item.variant_id];
            const hasSubscription = !!item.selling_plan_allocation;
            const canUpgrade = !hasSubscription && sp && sp.savePct > 0;
            html += `
            <div class="cart-drawer__item">
              ${item.image ? `<img src="${getSizedImageUrl(item.image, '128x')}" alt="${escapeHtml(item.title)}" class="cart-drawer__item-image" loading="lazy">` : ''}
              <div class="cart-drawer__item-info">
                <a href="${item.url}" class="cart-drawer__item-title">${escapeHtml(item.product_title)}</a>
                ${item.variant_title && item.variant_title !== 'Default Title' ? `<p style="font-size:0.75rem;color:var(--color-text-secondary)">${escapeHtml(item.variant_title)}</p>` : ''}
                ${hasSubscription ? `<p class="cart-drawer__subscription-badge"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg> ${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</p>` : ''}
                <p class="cart-drawer__item-price">${compareAtLine ? `<s class="cart-drawer__compare-price">${formatMoney(compareAtLine)}</s> ` : ''}${formatMoney(item.final_line_price)}</p>
                ${canUpgrade ? `
                <button class="cart-drawer__subscribe-upgrade" data-action="subscribe-upgrade" data-line="${i + 1}" data-variant="${item.variant_id}" data-selling-plan="${sp.sellingPlanId}" data-qty="${item.quantity}">
                  <span class="cart-drawer__subscribe-upgrade-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                  </span>
                  <span class="cart-drawer__subscribe-upgrade-text">
                    <strong>Subscribe & Save ${sp.savePct}%</strong>
                    <span>${formatMoney(sp.price * item.quantity)}/order &middot; Cancel anytime</span>
                  </span>
                </button>` : ''}
                <div class="cart-drawer__item-qty">
                  <button data-action="cart-minus" data-line="${i + 1}" data-qty="${item.quantity}" aria-label="Decrease">-</button>
                  <span>${item.quantity}</span>
                  <button data-action="cart-plus" data-line="${i + 1}" data-qty="${item.quantity}" aria-label="Increase">+</button>
                </div>
              </div>
            </div>`;
          });
          drawerBody.innerHTML = html;
        });
        if (drawerFooter) drawerFooter.style.display = '';
      }
    }

    // Update shipping bar
    updateShippingBar(cart.total_price);

    // Update window.theme.cartItemCount
    window.theme.cartItemCount = cart.item_count;
  });

  function renderEmptyCart(container) {
    const reviews = [
      { name: 'Patricia', age: 51, quote: 'Now I understand why Korean skincare is so popular. Nothing else has come close to these results.', image: '' },
      { name: 'Margaret', age: 58, quote: "The pictures don't lie \u2014 my 58-year-old skin looks better than it has in a decade!", image: '' },
      { name: 'Diana', age: 43, quote: 'My stubborn sun spots have visibly faded in just two weeks!', image: '' }
    ];
    const stars = '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
    const fiveStars = Array(5).fill(stars).join('');

    let html = `
      <div class="cart-empty">
        <p class="cart-empty__heading">Your cart is empty</p>
        <p class="cart-empty__subheading">Add one of our bestsellers:</p>
        <div class="cart-empty__product" data-empty-cart-product></div>
        <a href="/collections/all" class="btn btn--primary cart-empty__shop-btn">Shop All Products</a>
        <div class="cart-empty__reviews">
          <p class="cart-empty__reviews-heading">What our customers are saying:</p>
          <div class="cart-empty__carousel" data-cart-review-carousel>
            <div class="cart-empty__carousel-track">
              ${reviews.map((r, i) => `
                <div class="cart-empty__review${i === 0 ? ' is-active' : ''}">
                  ${r.image ? `<img src="${r.image}" alt="${r.name}" class="cart-empty__review-img" loading="lazy">` : ''}
                  <p class="cart-empty__review-quote">\u201c${r.quote}\u201d</p>
                  <div class="cart-empty__review-meta">
                    <span class="cart-empty__review-stars">${fiveStars}</span>
                    <span>\u2014 ${r.name}, ${r.age}</span>
                  </div>
                </div>
              `).join('')}
            </div>
            <div class="cart-empty__carousel-dots">
              ${reviews.map((_, i) => `<button class="cart-empty__carousel-dot${i === 0 ? ' is-active' : ''}" data-cart-review-dot="${i}" aria-label="Review ${i + 1}"></button>`).join('')}
            </div>
          </div>
        </div>
      </div>`;
    container.innerHTML = html;

    // Review carousel logic
    const carousel = container.querySelector('[data-cart-review-carousel]');
    if (carousel) {
      let current = 0;
      const slides = carousel.querySelectorAll('.cart-empty__review');
      const dots = carousel.querySelectorAll('[data-cart-review-dot]');
      let autoplay = setInterval(next, 4000);

      function goTo(idx) {
        slides[current].classList.remove('is-active');
        dots[current].classList.remove('is-active');
        current = idx;
        slides[current].classList.add('is-active');
        dots[current].classList.add('is-active');
      }

      function next() {
        goTo((current + 1) % slides.length);
      }

      dots.forEach(dot => {
        dot.addEventListener('click', () => {
          clearInterval(autoplay);
          goTo(parseInt(dot.dataset.cartReviewDot));
          autoplay = setInterval(next, 4000);
        });
      });
    }

    // Fetch bestselling product
    fetch('/products/collagen-night-wrapping-mask-1.js')
      .then(r => r.json())
      .then(product => {
        if (!product) return;
        const el = container.querySelector('[data-empty-cart-product]');
        if (!el) return;
        const variant = product.variants[0];
        const img = product.images && product.images[0] ? product.images[0] : (product.featured_image || '');
        const compareAt = variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : null;
        el.innerHTML = `
          <a href="/products/${product.handle}" class="cart-empty__product-link">
            ${img ? `<img src="${img}" alt="${escapeHtml(product.title)}" class="cart-empty__product-img" loading="lazy">` : ''}
            <div class="cart-empty__product-info">
              <p class="cart-empty__product-title">${escapeHtml(product.title)}</p>
              <p class="cart-empty__product-price">
                ${compareAt ? `<s class="cart-drawer__compare-price">${formatMoney(compareAt)}</s> ` : ''}
                ${formatMoney(variant.price)}
              </p>
            </div>
          </a>
          <button class="cart-empty__add-btn" data-add-to-cart data-variant-id="${variant.id}">+ Add to Cart</button>`;
      })
      .catch(() => {});
  }

  function updateShippingBar(totalPrice) {
    const threshold = window.theme.freeShippingThreshold;
    const bar = document.querySelector('[data-shipping-bar]');
    const fill = document.querySelector('[data-shipping-bar-fill]');
    const text = document.querySelector('[data-shipping-bar-text]');
    if (!fill || !text) return;

    if (!threshold || threshold <= 0) {
      if (bar) bar.style.display = 'none';
      return;
    }
    if (bar) bar.style.display = '';

    const pct = Math.min((totalPrice / threshold) * 100, 100);
    fill.style.width = pct + '%';

    if (totalPrice >= threshold) {
      text.textContent = "You've unlocked free shipping!";
      fill.style.background = 'var(--color-success)';
    } else {
      const remaining = formatMoney(threshold - totalPrice);
      text.textContent = `You're ${remaining} away from free shipping!`;
    }
  }

  /* ========================================
     3. MONEY FORMATTING
     ======================================== */
  function formatMoney(cents) {
    if (isNaN(cents) || cents == null) cents = 0;
    const format = window.theme.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.floor(cents / 100).toString();
    const amountWithComma = amount.replace('.', ',');

    return format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  /* ========================================
     4. DRAWER SYSTEM
     ======================================== */
  window.openDrawer = openDrawer;
  function openDrawer(drawerId) {
    const drawer = document.getElementById(drawerId);
    const overlay = document.querySelector('[data-drawer-overlay]');
    if (!drawer) return;

    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Focus trap
    const focusable = drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable.length) focusable[0].focus();
  }

  function closeAllDrawers() {
    document.querySelectorAll('.drawer.is-open').forEach((d) => d.classList.remove('is-open'));
    const overlay = document.querySelector('[data-drawer-overlay]');
    if (overlay) overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /* ========================================
     5. EVENT DELEGATION
     ======================================== */
  document.addEventListener('click', (e) => {
    const target = e.target.closest('[data-action]');
    if (!target) {
      // Check for other clickable elements
      if (e.target.closest('[data-drawer-overlay]')) {
        closeAllDrawers();
        return;
      }
      if (e.target.closest('[data-drawer-close]')) {
        closeAllDrawers();
        return;
      }
      if (e.target.closest('[data-add-to-cart]')) {
        handleQuickAdd(e);
        return;
      }
      if (e.target.closest('[data-slider-dot]')) {
        handleSliderDot(e);
        return;
      }
      if (e.target.closest('[data-video-wrapper]')) {
        handleVideoPlay(e);
        return;
      }
      if (e.target.closest('[data-thumb-index]')) {
        handleThumbnailClick(e);
        return;
      }
      if (e.target.closest('.variant-selector__option')) {
        handleVariantSelect(e);
        return;
      }
      if (e.target.closest('[data-purchase-type-card]')) {
        handlePurchaseTypeToggle(e);
        return;
      }
      if (e.target.closest('[data-frequency-option]')) {
        handleFrequencySelect(e);
        return;
      }
      if (e.target.closest('[data-bundle-card]')) {
        handleBundleSelect(e);
        return;
      }
      if (e.target.closest('[data-quantity-minus]')) {
        handleQuantity(e, -1);
        return;
      }
      if (e.target.closest('[data-quantity-plus]')) {
        handleQuantity(e, 1);
        return;
      }
      return;
    }

    const action = target.dataset.action;

    switch (action) {
      case 'open-cart-drawer':
        e.preventDefault();
        openDrawer('cart-drawer');
        break;
      case 'open-mobile-menu':
        e.preventDefault();
        openDrawer('mobile-menu');
        break;
      case 'cart-minus': {
        const line = parseInt(target.dataset.line);
        const qty = parseInt(target.dataset.qty);
        Cart.change(line, Math.max(0, qty - 1));
        break;
      }
      case 'cart-plus': {
        const line = parseInt(target.dataset.line);
        const qty = parseInt(target.dataset.qty);
        Cart.change(line, qty + 1);
        break;
      }
      case 'subscribe-upgrade': {
        const btn = target.closest('[data-action="subscribe-upgrade"]');
        if (!btn || btn.disabled) break;
        btn.disabled = true;
        btn.style.opacity = '0.5';
        const line = parseInt(btn.dataset.line);
        const variantId = parseInt(btn.dataset.variant);
        const sellingPlan = parseInt(btn.dataset.sellingPlan);
        const qty = parseInt(btn.dataset.qty);
        // Remove old one-time item, add subscription version
        Cart.change(line, 0).then(() => {
          return Cart.add(variantId, qty, sellingPlan);
        }).catch(() => {
          btn.disabled = false;
          btn.style.opacity = '';
        });
        break;
      }
    }
  });

  // Escape key closes drawers
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAllDrawers();
  });

  /* ========================================
     6. QUICK ADD TO CART
     ======================================== */
  async function handleQuickAdd(e) {
    e.preventDefault();
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;

    if (btn.hasAttribute('data-has-variants')) {
      // Redirect to product page for products with variants
      const card = btn.closest('.product-card');
      const link = card?.querySelector('.product-card__link');
      if (link) window.location.href = link.href;
      return;
    }

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    try {
      await Cart.add(parseInt(variantId));
      btn.textContent = 'Added!';
      openDrawer('cart-drawer');
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1500);
    } catch {
      btn.textContent = 'Error';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1500);
    }
  }

  /* ========================================
     7. PRODUCT FORM (PDP)
     ======================================== */
  const productForm = document.querySelector('[data-product-form]');
  if (productForm) {
    productForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(productForm);
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity')) || 1;
      const sellingPlan = formData.get('selling_plan') || null;
      const btn = productForm.querySelector('[data-add-to-cart-btn]');

      if (!btn || btn.disabled) return;

      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding...';

      try {
        await Cart.add(parseInt(variantId), quantity, sellingPlan);
        btn.textContent = 'Added!';
        if (!document.querySelector('[data-upsell-popup]')) {
          openDrawer('cart-drawer');
        }
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      } catch {
        btn.textContent = 'Error - Try again';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      }
    });
  }

  /* ========================================
     8. VARIANT SELECTOR (PDP)
     ======================================== */
  function handleVariantSelect(e) {
    const option = e.target.closest('.variant-selector__option');
    if (!option || option.classList.contains('is-unavailable')) return;

    // Update selected state
    const container = option.closest('.variant-selector__options');
    container.querySelectorAll('.variant-selector__option').forEach((o) => o.classList.remove('is-selected'));
    option.classList.add('is-selected');

    // Build selected options array
    const selectors = document.querySelectorAll('.variant-selector');
    const selectedOptions = [];
    selectors.forEach((sel) => {
      const active = sel.querySelector('.variant-selector__option.is-selected');
      if (active) selectedOptions.push(active.dataset.optionValue);
    });

    // Find matching variant
    const variantSelect = document.querySelector('[data-variant-select]');
    if (!variantSelect) return;

    for (const opt of variantSelect.options) {
      const variantTitle = opt.textContent.trim().split(' - ')[0];
      const variantOptions = variantTitle.split(' / ');
      const matches = selectedOptions.every((so, i) => variantOptions[i]?.trim() === so);
      if (matches) {
        variantSelect.value = opt.value;

        // Update price
        const priceContainer = document.querySelector('.s-main-product__price');
        if (priceContainer) {
          const price = opt.dataset.price;
          const comparePrice = opt.dataset.comparePrice;
          const onSale = opt.dataset.onSale === 'true';
          let html = '<div class="price' + (onSale ? ' price--on-sale' : '') + '">';
          if (onSale && comparePrice) {
            html += '<span class="price__compare-at"><s>' + comparePrice + '</s></span>';
          }
          html += '<span class="price__regular">' + price + '</span></div>';
          priceContainer.innerHTML = html;
        }

        // Update add to cart button
        const addBtn = document.querySelector('[data-add-to-cart-btn]');
        if (addBtn) {
          const available = opt.dataset.available === 'true';
          addBtn.disabled = !available;
          const price = opt.dataset.price;
          if (available && price) {
            const atcPrice = addBtn.querySelector('[data-atc-price]');
            if (atcPrice) {
              atcPrice.textContent = price;
            } else {
              addBtn.innerHTML = 'Add to Cart - <span data-atc-price>' + price + '</span>';
            }
          } else if (!available) {
            addBtn.textContent = 'Sold Out';
          }
        }
        break;
      }
    }
  }

  /* ========================================
     8a2. AUTO-SET SELLING PLAN BASED ON BUNDLE QTY
     ======================================== */
  function setSellingPlanForBundle() {
    var selectedCard = document.querySelector('[data-bundle-card].is-selected');
    var sellingPlanInput = document.querySelector('[data-selling-plan-input]');
    if (!selectedCard || !sellingPlanInput) return;

    // Primary: use the selling plan ID stored directly on the variant card
    var cardPlanId = selectedCard.dataset.sellingPlanId;
    if (cardPlanId) {
      sellingPlanInput.value = cardPlanId;
      return;
    }

    // Fallback: look for a selling plan map element that matches the bundle qty (months)
    var bundleQty = parseInt(selectedCard.dataset.bundleQty) || 1;
    var planMap = document.querySelector('[data-selling-plan-map][data-months="' + bundleQty + '"]');
    if (planMap) {
      sellingPlanInput.value = planMap.dataset.planId;
    } else {
      // Last resort: use the first selling plan available
      var firstPlan = document.querySelector('[data-selling-plan-map]');
      if (firstPlan) {
        sellingPlanInput.value = firstPlan.dataset.planId;
      }
    }
  }

  /* ========================================
     8b. BUNDLE SELECTOR
     ======================================== */
  function handleBundleSelect(e) {
    const card = e.target.closest('[data-bundle-card]');
    if (!card) return;

    // Update selected state
    const selector = card.closest('[data-bundle-selector]');
    selector.querySelectorAll('[data-bundle-card]').forEach(function(c) {
      c.classList.remove('is-selected');
    });
    card.classList.add('is-selected');

    // Check the radio
    var radio = card.querySelector('[data-bundle-radio]');
    if (radio) radio.checked = true;

    // Check if we're in subscribe mode
    var subscribeCard = document.querySelector('[data-purchase-type-card="subscribe"].is-selected');
    var isSubscribeMode = !!subscribeCard;

    if (isSubscribeMode) {
      // Use frequency selector if present (original template), else v2 card data
      var freqSelector = document.querySelector('[data-frequency-selector]');
      if (freqSelector) {
        var selectedFreq = freqSelector.querySelector('[data-frequency-option].is-selected');
        var sellingPlanInput = document.querySelector('[data-selling-plan-input]');
        if (selectedFreq && sellingPlanInput) {
          sellingPlanInput.value = selectedFreq.dataset.sellingPlanId;
        }
      } else {
        setSellingPlanForBundle();
      }
      updateBundlePrices('subscribe');
    } else {
      updateBundlePrices('onetime');
    }

    // Update add to cart button availability
    var available = card.dataset.available !== 'false';
    var addBtn = document.querySelector('[data-add-to-cart-btn]');
    if (addBtn) {
      addBtn.disabled = !available;
      if (!available) {
        addBtn.textContent = 'Sold Out';
      }
    }

    // Update product slider to variant image if applicable
    var variantId = card.dataset.variantId;
    if (window.theme && window.theme.variantImages && window.theme.variantImages[variantId] !== undefined) {
      var slideIndex = window.theme.variantImages[variantId];
      if (typeof window.goToSlide === 'function') window.goToSlide(slideIndex);
    }
  }

  /* ========================================
     8c. PURCHASE TYPE TOGGLE (one-time vs subscribe)
     ======================================== */
  function handlePurchaseTypeToggle(e) {
    var card = e.target.closest('[data-purchase-type-card]');
    if (!card) return;

    var type = card.dataset.purchaseTypeCard;
    var container = card.closest('[data-purchase-type]');
    container.querySelectorAll('[data-purchase-type-card]').forEach(function(c) {
      c.classList.remove('is-selected');
    });
    card.classList.add('is-selected');

    var freqSelector = document.querySelector('[data-frequency-selector]');
    var sellingPlanInput = document.querySelector('[data-selling-plan-input]');

    // Toggle "Ships every X months" text and count visibility
    document.querySelectorAll('.v2-tier-card__ships').forEach(function(el) {
      el.style.display = type === 'subscribe' ? 'block' : 'none';
    });
    document.querySelectorAll('.v2-tier-card__count').forEach(function(el) {
      el.style.display = type === 'subscribe' ? 'none' : 'block';
    });

    if (type === 'subscribe') {
      if (freqSelector) {
        // Original template: show frequency selector and use its selected plan
        freqSelector.style.display = '';
        var selectedFreq = freqSelector.querySelector('[data-frequency-option].is-selected');
        if (selectedFreq && sellingPlanInput) {
          sellingPlanInput.value = selectedFreq.dataset.sellingPlanId;
        }
      } else {
        // V2 template: auto-set selling plan from variant card data
        setSellingPlanForBundle();
      }
    } else {
      if (freqSelector) freqSelector.style.display = 'none';
      if (sellingPlanInput) sellingPlanInput.value = '';
    }

    updateBundlePrices(type);
  }

  function handleFrequencySelect(e) {
    var option = e.target.closest('[data-frequency-option]');
    if (!option) return;

    var container = option.closest('.frequency-selector__options');
    container.querySelectorAll('[data-frequency-option]').forEach(function(o) {
      o.classList.remove('is-selected');
    });
    option.classList.add('is-selected');

    var sellingPlanInput = document.querySelector('[data-selling-plan-input]');
    if (sellingPlanInput) {
      sellingPlanInput.value = option.dataset.sellingPlanId;
    }

    updateBundlePrices('subscribe');
  }

  function updateBundlePrices(purchaseType) {
    var cards = document.querySelectorAll('[data-bundle-card]');
    var selectedFreq = document.querySelector('[data-frequency-option].is-selected');
    var bundleSelector = document.querySelector('[data-bundle-selector]');
    var unitType = bundleSelector ? (bundleSelector.dataset.unitType || 'Jar') : 'Jar';

    // Update per-unit prices on bundle cards
    cards.forEach(function(card) {
      var singleJarCents = parseInt(card.dataset.singleJarPriceCents) || 0;
      var variantPrice = parseInt(card.dataset.variantPriceCents) || 0;
      var bundleQty = parseInt(card.dataset.bundleQty) || 1;
      var priceEl = card.querySelector('[data-bundle-price]');
      var compareEl = card.querySelector('[data-bundle-compare]');
      var saveBadge = card.querySelector('[data-bundle-save-badge]');

      // One-time per-unit: Shopify variant price divided by qty
      var onetimePerUnit = Math.round(variantPrice / bundleQty);

      // Real subscription prices from Recharge selling plan allocations (v2 template)
      var subTotalCents = parseInt(card.dataset.subPriceCents) || 0;
      var subPerUnitCents = parseInt(card.dataset.subPerUnitCents) || 0;
      var subSavePct = parseInt(card.dataset.subSavePct) || 0;
      var onetimeSavePct = parseInt(card.dataset.bundleSavePct) || 0;

      // Fallback for original template: calculate from frequency selector discount
      if (purchaseType === 'subscribe' && subTotalCents === 0 && selectedFreq) {
        var discountType = selectedFreq.dataset.discountType;
        var discountValue = parseFloat(selectedFreq.dataset.discountValue) || 0;
        if (discountType === 'percentage' && discountValue > 0) {
          subTotalCents = Math.round(variantPrice * (100 - discountValue) / 100);
          subPerUnitCents = Math.round(subTotalCents / bundleQty);
          subSavePct = Math.round(discountValue);
        } else if (discountType === 'fixed_amount' && discountValue > 0) {
          subTotalCents = variantPrice - Math.round(discountValue * 100);
          subPerUnitCents = Math.round(subTotalCents / bundleQty);
          if (variantPrice > 0) subSavePct = Math.round((discountValue * 100) / variantPrice * 100);
        }
      }

      if (purchaseType === 'subscribe' && subTotalCents > 0) {
        // Subscribe: use real Recharge subscription price
        if (priceEl) priceEl.textContent = formatMoney(subPerUnitCents);
        // Compare-at: the one-time per-unit price
        if (compareEl) {
          compareEl.innerHTML = '<s>' + formatMoney(onetimePerUnit) + '/' + unitType + '</s>';
          compareEl.style.display = '';
        }
        // Update save badge with subscription save %
        if (saveBadge) {
          if (subSavePct > 0) {
            saveBadge.textContent = 'YOU SAVE ' + subSavePct + '%';
            saveBadge.style.display = '';
          } else {
            saveBadge.style.display = 'none';
          }
        }
      } else {
        // One-time: use Shopify variant price / qty
        if (priceEl) priceEl.textContent = formatMoney(onetimePerUnit);
        if (compareEl) {
          if (bundleQty > 1 && singleJarCents > onetimePerUnit) {
            compareEl.innerHTML = '<s>' + formatMoney(singleJarCents) + '/' + unitType + '</s>';
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }
        // Restore one-time save badge
        if (saveBadge) {
          if (onetimeSavePct > 0) {
            saveBadge.textContent = 'YOU SAVE ' + onetimeSavePct + '%';
            saveBadge.style.display = '';
          } else {
            saveBadge.style.display = 'none';
          }
        }
      }
    });

    // Update total prices on purchase type cards, main price display, and ATC button
    var selectedCard = document.querySelector('[data-bundle-card].is-selected');
    if (selectedCard) {
      var variantPrice = parseInt(selectedCard.dataset.variantPriceCents) || 0;
      var subTotalCents = parseInt(selectedCard.dataset.subPriceCents) || 0;

      // One-time total: raw Shopify variant price
      var onetimeTotal = variantPrice;

      // Fallback for original template: calculate from frequency selector discount
      if (subTotalCents === 0 && selectedFreq) {
        var discountType = selectedFreq.dataset.discountType;
        var discountValue = parseFloat(selectedFreq.dataset.discountValue) || 0;
        if (discountType === 'percentage' && discountValue > 0) {
          subTotalCents = Math.round(variantPrice * (100 - discountValue) / 100);
        } else if (discountType === 'fixed_amount' && discountValue > 0) {
          subTotalCents = variantPrice - Math.round(discountValue * 100);
        }
      }

      // Subscribe total: real Recharge price, fallback to one-time
      var subscribeTotal = subTotalCents > 0 ? subTotalCents : onetimeTotal;

      // The price to display depends on which purchase type is active
      var displayTotal = purchaseType === 'subscribe' ? subscribeTotal : onetimeTotal;

      // Main price display
      var priceContainer = document.querySelector('.s-main-product__price');
      if (priceContainer) {
        var onSale = (purchaseType === 'subscribe' && subscribeTotal < onetimeTotal) || selectedCard.dataset.onSale === 'true';
        var html = '<div class="price' + (onSale ? ' price--on-sale' : '') + '">';
        if (purchaseType === 'subscribe' && subscribeTotal < onetimeTotal) {
          html += '<span class="price__compare-at"><s>' + formatMoney(onetimeTotal) + '</s></span>';
        } else if (selectedCard.dataset.onSale === 'true') {
          html += '<span class="price__compare-at"><s>' + selectedCard.dataset.comparePrice + '</s></span>';
        }
        html += '<span class="price__regular">' + formatMoney(displayTotal) + '</span></div>';
        priceContainer.innerHTML = html;
      }

      // ATC button
      var addBtn = document.querySelector('[data-add-to-cart-btn]');
      if (addBtn && !addBtn.disabled) {
        var atcPrice = addBtn.querySelector('[data-atc-price]');
        if (atcPrice) {
          atcPrice.textContent = formatMoney(displayTotal);
        } else {
          addBtn.innerHTML = 'Add to Cart - <span data-atc-price>' + formatMoney(displayTotal) + '</span>';
        }
      }

      // Purchase type card prices
      var onetimePriceEl = document.querySelector('[data-onetime-price]');
      var onetimeCompareEl = document.querySelector('[data-onetime-compare]');
      if (onetimePriceEl) onetimePriceEl.textContent = formatMoney(onetimeTotal);
      if (onetimeCompareEl) {
        if (selectedCard.dataset.onSale === 'true') {
          onetimeCompareEl.textContent = selectedCard.dataset.comparePrice;
          onetimeCompareEl.style.display = '';
        } else {
          onetimeCompareEl.style.display = 'none';
        }
      }

      var subPriceEl = document.querySelector('[data-subscribe-price]');
      var subCompareEl = document.querySelector('[data-subscribe-compare]');
      if (subPriceEl) subPriceEl.textContent = formatMoney(subscribeTotal);
      if (subCompareEl) {
        subCompareEl.textContent = formatMoney(onetimeTotal);
        subCompareEl.style.display = subscribeTotal < onetimeTotal ? '' : 'none';
      }
    }
  }

  /* ========================================
     9. QUANTITY SELECTOR
     ======================================== */
  function handleQuantity(e, delta) {
    e.preventDefault();
    const btn = e.target.closest('[data-quantity-minus], [data-quantity-plus]');
    const container = btn.closest('.quantity-selector');
    const input = container.querySelector('.quantity-selector__input, [data-quantity-input]');
    if (!input) return;

    const current = parseInt(input.value) || 1;
    const min = parseInt(input.min) || 0;
    const newVal = Math.max(min, current + delta);
    input.value = newVal;
    input.dispatchEvent(new Event('change'));
  }

  /* ========================================
     10. SLIDER WITH SCROLL-SNAP + DOTS
     ======================================== */
  document.querySelectorAll('[data-slider]').forEach((track) => {
    const dotsContainer = track.closest('section')?.querySelector('[data-slider-dots]');
    if (!dotsContainer) return;

    const dots = dotsContainer.querySelectorAll('[data-slider-dot]');
    const slides = track.querySelectorAll('[data-slide]');

    // IntersectionObserver to track active slide
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Array.from(slides).indexOf(entry.target);
            dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
          }
        });
      },
      { root: track, threshold: 0.6 }
    );

    slides.forEach((slide) => observer.observe(slide));

    // Click dot to scroll
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.sliderDot);
        if (slides[index]) {
          slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });
    });
  });

  /* ========================================
     11. SLIDER DOT HANDLER (fallback)
     ======================================== */
  function handleSliderDot(e) {
    const dot = e.target.closest('[data-slider-dot]');
    if (!dot) return;
    const index = parseInt(dot.dataset.sliderDot);
    const track = dot.closest('section')?.querySelector('[data-slider]');
    const slides = track?.querySelectorAll('[data-slide]');
    if (slides?.[index]) {
      slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  /* ========================================
     12. LAZY VIDEO (click poster to load iframe)
     ======================================== */
  function handleVideoPlay(e) {
    const wrapper = e.target.closest('[data-video-wrapper]');
    if (!wrapper || wrapper.querySelector('iframe')) return;

    const url = wrapper.dataset.videoUrl;
    if (!url) return;

    let embedUrl = '';
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const id = url.match(/(?:v=|youtu\.be\/)([^&?]+)/)?.[1];
      if (id) embedUrl = `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
    } else if (url.includes('vimeo.com')) {
      const id = url.match(/vimeo\.com\/(\d+)/)?.[1];
      if (id) embedUrl = `https://player.vimeo.com/video/${id}?autoplay=1`;
    }

    if (embedUrl) {
      const iframe = document.createElement('iframe');
      iframe.src = embedUrl;
      iframe.className = 's-video__iframe';
      iframe.setAttribute('allow', 'autoplay; fullscreen');
      iframe.setAttribute('allowfullscreen', '');
      wrapper.innerHTML = '';
      wrapper.appendChild(iframe);
    }
  }

  /* ========================================
     13. BEFORE / AFTER SLIDER
     ======================================== */
  class BeforeAfterSlider extends HTMLElement {
    connectedCallback() {
      const slider = this.querySelector('[data-before-after-slider]');
      const afterImg = this.querySelector('.s-before-after__image--after');
      const handle = this.querySelector('[data-before-after-handle]');

      if (!slider || !afterImg) return;

      slider.addEventListener('input', () => {
        const val = slider.value;
        afterImg.style.clipPath = `inset(0 ${100 - val}% 0 0)`;
        if (handle) handle.style.left = val + '%';
      });
    }
  }

  if (!customElements.get('before-after-slider')) {
    customElements.define('before-after-slider', BeforeAfterSlider);
  }

  /* ========================================
     14. PRODUCT IMAGE SLIDER (PDP)
     ======================================== */
  function initProductSlider() {
    var gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;

    var slider = gallery.querySelector('[data-product-slider]');
    var slides = gallery.querySelectorAll('.s-main-product__slide');
    var thumbs = gallery.querySelectorAll('.s-main-product__thumb');
    var prevBtn = gallery.querySelector('[data-slider-prev]');
    var nextBtn = gallery.querySelector('[data-slider-next]');
    if (!slider || slides.length === 0) return;

    var currentIndex = 0;

    function goToSlide(index) {
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;
      currentIndex = index;
      slider.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
      thumbs.forEach(function(t, i) {
        t.classList.toggle('is-active', i === index);
      });
    }

    if (prevBtn) prevBtn.addEventListener('click', function() { goToSlide(currentIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function() { goToSlide(currentIndex + 1); });

    thumbs.forEach(function(thumb) {
      thumb.addEventListener('click', function() {
        goToSlide(parseInt(thumb.dataset.thumbIndex));
      });
    });

    // Sync active thumb on scroll
    var scrollTimeout;
    slider.addEventListener('scroll', function() {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(function() {
        var scrollLeft = slider.scrollLeft;
        var sliderWidth = slider.offsetWidth;
        var newIndex = Math.round(scrollLeft / sliderWidth);
        if (newIndex !== currentIndex && newIndex >= 0 && newIndex < slides.length) {
          currentIndex = newIndex;
          thumbs.forEach(function(t, i) {
            t.classList.toggle('is-active', i === currentIndex);
          });
        }
      }, 50);
    });

    // Touch/swipe support
    var touchStartX = 0;
    slider.addEventListener('touchstart', function(e) {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    slider.addEventListener('touchend', function(e) {
      var diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) {
        goToSlide(diff > 0 ? currentIndex + 1 : currentIndex - 1);
      }
    }, { passive: true });

    // Mouse drag-to-snap support
    var dragStartX = 0;
    var isDragging = false;
    slider.addEventListener('mousedown', function(e) {
      if (e.button !== 0) return;
      isDragging = true;
      dragStartX = e.pageX;
      slider.classList.add('is-dragging');
      e.preventDefault();
    });
    slider.addEventListener('mousemove', function(e) {
      if (!isDragging) return;
      e.preventDefault();
    });
    slider.addEventListener('mouseup', function(e) {
      if (!isDragging) return;
      isDragging = false;
      slider.classList.remove('is-dragging');
      var diff = dragStartX - e.pageX;
      if (Math.abs(diff) > 40) {
        goToSlide(diff > 0 ? currentIndex + 1 : currentIndex - 1);
      }
    });
    slider.addEventListener('mouseleave', function() {
      if (isDragging) {
        isDragging = false;
        slider.classList.remove('is-dragging');
      }
    });
  }

  function handleThumbnailClick(e) {
    // Kept for backwards compat — slider init handles clicks now
  }

  /* ========================================
     14b. DRAG-TO-SCROLL FOR ALL CAROUSELS
     ======================================== */
  function enableDragScroll(track) {
    if (!track) return;
    var isDown = false;
    var startX = 0;
    var scrollLeft = 0;

    track.addEventListener('mousedown', function(e) {
      if (e.button !== 0) return;
      isDown = true;
      track.classList.add('is-dragging');
      startX = e.pageX - track.offsetLeft;
      scrollLeft = track.scrollLeft;
      e.preventDefault();
    });

    track.addEventListener('mouseleave', function() {
      isDown = false;
      track.classList.remove('is-dragging');
    });

    track.addEventListener('mouseup', function() {
      isDown = false;
      track.classList.remove('is-dragging');
    });

    track.addEventListener('mousemove', function(e) {
      if (!isDown) return;
      var x = e.pageX - track.offsetLeft;
      var walk = (x - startX) * 1.5;
      track.scrollLeft = scrollLeft - walk;
    });
  }

  // Apply drag-to-scroll on continuous carousels (not product slider — it snaps to slides)
  document.querySelectorAll('[data-reviews-track], [data-press-track], [data-slider]').forEach(enableDragScroll);

  /* ========================================
     15. STICKY HEADER SCROLL
     ======================================== */
  const header = document.querySelector('[data-header]');
  if (header) {
    let lastScroll = 0;
    window.addEventListener(
      'scroll',
      () => {
        const scrollY = window.scrollY;
        header.classList.toggle('is-scrolled', scrollY > 10);
        lastScroll = scrollY;
      },
      { passive: true }
    );
  }

  /* ========================================
     16. COLLECTION SORT
     ======================================== */
  const sortSelect = document.querySelector('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  }

  /* ========================================
     17. PRODUCT IMAGE SLIDER INIT
     ======================================== */
  initProductSlider();

  /* ========================================
     17b. INIT BUNDLE PRICES ON LOAD
     ======================================== */
  (function initBundlePrices() {
    var selectedCard = document.querySelector('[data-bundle-card].is-selected');
    if (selectedCard) {
      var subscribeActive = document.querySelector('[data-purchase-type-card="subscribe"].is-selected');
      updateBundlePrices(subscribeActive ? 'subscribe' : 'onetime');
    }
  })();

  /* ========================================
     FEATURED VIDEO MODAL (inline PDP)
     ======================================== */
  (function() {
    var modal = document.querySelector('[data-fv-modal]');
    if (!modal) return;
    var player = modal.querySelector('[data-fv-player]');
    var closeBtns = modal.querySelectorAll('[data-fv-close]');

    document.querySelectorAll('[data-fv-trigger]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var tpl = btn.querySelector('[data-fv-source]');
        if (!tpl) return;
        player.innerHTML = tpl.innerHTML;
        modal.classList.add('is-active');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        var video = player.querySelector('video');
        if (video) { video.muted = false; video.play().catch(function() {}); }
      });
    });

    function closeModal() {
      modal.classList.remove('is-active');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      var video = player.querySelector('video');
      if (video) { video.pause(); }
      setTimeout(function() { player.innerHTML = ''; }, 300);
    }

    closeBtns.forEach(function(btn) { btn.addEventListener('click', closeModal); });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.classList.contains('is-active')) closeModal();
    });
  })();

  /* ========================================
     UPSELL — add to cart via AJAX
     ======================================== */
  document.querySelectorAll('[data-upsell-add]').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var variantId = btn.getAttribute('data-upsell-variant-id');
      if (!variantId) return;
      var originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding...';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      })
      .then(function(res) { return res.json(); })
      .then(function() {
        btn.textContent = 'Added ✓';
        setTimeout(function() {
          btn.disabled = false;
          btn.textContent = originalText;
        }, 2000);
        // Refresh cart drawer and open it
        fetch('/cart.js')
          .then(function(r) { return r.json(); })
          .then(function(cart) {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
            openDrawer('cart-drawer');
          });
      })
      .catch(function() {
        btn.disabled = false;
        btn.textContent = originalText;
      });
    });
  });

  /* ========================================
     ACCORDION — only one open at a time
     ======================================== */
  document.querySelectorAll('.s-main-product__accordion').forEach(function(accordion) {
    function animateClose(details) {
      details.classList.add('is-closing');
      details.addEventListener('animationend', function handler() {
        details.classList.remove('is-closing');
        details.removeAttribute('open');
        details.removeEventListener('animationend', handler);
      });
    }

    accordion.addEventListener('click', function(e) {
      var summary = e.target.closest('summary');
      if (!summary) return;
      var details = summary.parentElement;

      // If clicking an open item, animate it closed
      if (details.open) {
        e.preventDefault();
        animateClose(details);
        return;
      }

      // Close other open items with animation
      accordion.querySelectorAll('details[open]').forEach(function(d) {
        if (d !== details) animateClose(d);
      });
    });
  });

  /* ========================================
     UTILITIES
     ======================================== */
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function getSizedImageUrl(url, size) {
    if (!url) return '';
    return url.replace(/\.([^.]+)$/, `_${size}.$1`);
  }

  /* ========================================
     INIT: Hydrate cart drawer on page load
     ======================================== */
  Cart.refresh();
})();
